import cn from 'classnames';
import Image from 'next/image';
import type { FC } from 'react';
import React from 'react';

import ALink from 'components/basic/ALink';
import LearnMoreLink from 'components/basic/LearnMoreLink';
import Typograf from 'components/basic/Typograf';
import { Blockquote, BodyL, H1, H3, BodyM } from 'components/basic/Typography';
import CheckedList from 'components/basic/Typography/CheckedList';

import s from './Hero.module.scss';

// TODO: Убрать disable и переписать на 2 компонента
/* eslint-disable react/jsx-max-depth */
const Hero: FC = () => {
  return (
    <Typograf>
      <div className="mb-sm-10 mb-lg-20">
        <div className={s.wrapper}>
          <div className={s.image}>
            <Image
              width={696}
              height={500}
              src="/pagesImages/landing/for-researches/hero/hero.svg"
              alt="researches"
              layout="intrinsic"
              priority
            />
          </div>
          <div className={s.text}>
            <H1 className="mb-sm-6 mb-md-4 mb-lg-6">
              Level up your study with an accurate and reliable HRV tracking
              tool
            </H1>
            <div className={s.links}>
              <div className={s.linksColumns}>
                <BodyM className={s.link}>
                  <a
                    href="#Welltory-is-an-easy-to-use-app"
                    className={s.linkText}
                  >
                    Welltory is an easy-to-use app
                  </a>
                </BodyM>
                <BodyM className={s.link}>
                  <a href="#Welltory-is-a-reliable-tool" className={s.linkText}>
                    Welltory is a reliable tool
                  </a>
                </BodyM>
                <BodyM className={s.link}>
                  <a
                    href="#How-do-universities-use- the-Welltory-app"
                    className={s.linkText}
                  >
                    How do universities use  the Welltory app
                  </a>
                </BodyM>
                <BodyM className={s.link}>
                  <a
                    href="#Ways-of-using-HRV-in-research"
                    className={s.linkText}
                  >
                    Ways of using HRV in research
                  </a>
                </BodyM>
              </div>
              <div className={s.linksColumns}>
                <BodyM className={s.link}>
                  <a href="#Eight-benefits-for-partners" className={s.linkText}>
                    Eight benefits for partners
                  </a>
                </BodyM>
                <BodyM className={s.link}>
                  <a href="#Privacy-and-safety" className={s.linkText}>
                    Privacy and safety
                  </a>
                </BodyM>
                <BodyM className={s.link}>
                  <a
                    href="#Exploring-data-from-Welltory"
                    className={s.linkText}
                  >
                    Exploring data from Welltory
                  </a>
                </BodyM>
                <BodyM className={s.link}>
                  <a href="#FAQ" className={s.linkText}>
                    FAQ
                  </a>
                </BodyM>
              </div>
            </div>
          </div>
        </div>
      </div>
      <H3 className={cn(s.subHeader, 'mb-sm-12 mb-xm-8 mb-lg-16 mb-xl-20')}>
        We provide accurate and detailed HRV analysis for short- and long-term
        measurements and for all kinds of study protocols. Welltory supports
        both PPG and ECG measurements taken with BLE heart rate monitors like
        Polar, Apple Watch, Samsung HRM, and even your phone’s camera.
      </H3>

      <div className={cn('grid', s.colsWrapper)}>
        <div className="g-col-6">
          <H1 className="mb-sm-4 mb-lg-6">
            {/* @ts-ignore */}
            <a name="Welltory-is-an-easy-to-use-app">
              <span className="md-hidden">Welltory is an easy-to-use app</span>
              <span className="md-visible not-md-hidden">
                Research tools have never been so easy and accessible
              </span>
            </a>
          </H1>
          <BodyL className="mb-sm-4 mb-lg-12">
            There is no necessity to align huge budgets or buy special medical
            equipment. With Welltory, you only need participants with
            smartphones and a research license.
          </BodyL>

          <Blockquote author="Douglas Adams" type="bodyL">
            The invention of the scientific method and science is, I’m sure
            we’ll all agree, the most powerful intellectual idea, the most
            powerful framework for thinking and investigating and understanding
            and challenging the world around us that there is.
          </Blockquote>
          <LearnMoreLink
            className="mt-sm-4 mt-lg-6 mb-sm-23 mb-xm-0"
            header="Learn more about the science behind Welltory"
            href="https://welltory.com/science/"
          />
        </div>
        <div className="g-col-6">
          <H1 className="mb-sm-4 mb-lg-6">
            {/* @ts-ignore */}
            <a name="Welltory-is-a-reliable-tool">
              Why Welltory is a reliable HRV tracking tool
            </a>
          </H1>
          <CheckedList>
            <BodyL className="mb-sm-6 mb-lg-9">
              <ALink
                href="https://www.sciencedirect.com/science/article/abs/pii/S0169260722000815?via%3Dihub#"
                target="_blank"
              >
                It was proved that Welltory is a valid and reliable
              </ALink>{' '}
              smartphone application and can be used to monitor HRV among elite
              endurance athletes.
            </BodyL>
          </CheckedList>
          <CheckedList>
            <BodyL className="mb-sm-6 mb-lg-9">
              Our research team{' '}
              <ALink
                href="https://ieeexplore.ieee.org/document/8856540"
                target="_blank"
              >
                designs professional methods to analyze
              </ALink>{' '}
              Photoplethysmography (PPG) measurements of heart rate variability
              using a phone camera.
            </BodyL>
          </CheckedList>
          <CheckedList>
            <BodyL className="mb-sm-6 mb-lg-9">
              We’ve also published{' '}
              <ALink
                href="https://www.researchgate.net/publication/336327954_Smartphone_PPG_signal_processing_quality_assessment_and_impact_on_HRV_parameters"
                target="_blank"
              >
                research
              </ALink>{' '}
              on our HRV signal processing and quality assessment method.
            </BodyL>
          </CheckedList>

          <CheckedList>
            <BodyL>
              The accuracy of measurements taken with a smartphone camera has
              been proven — such measurements are{' '}
              <ALink
                href="https://medium.com/welltory/camera-vs-polar-heart-rate-monitor-68902fb6a2d7"
                target="_blank"
              >
                on par
              </ALink>{' '}
              with the Polar H7 chest strap.
            </BodyL>
          </CheckedList>
        </div>
      </div>
    </Typograf>
  );
};

export default Hero;
